import * as THREE from 'three';
import { solidworksDefaultPreset } from '../imported-model/render/solidworks-default-preset';

/**
 * `FeatureFace` 材质状态枚举。
 *
 * 设计说明：
 * 1. `FeatureFace` 不属于实体主网格，而是面级交互代理；
 * 2. 因此它的渲染语义应独立于实体主材质，并统一由材质层收口；
 * 3. 这里仅保留 `FeatureFace` 真正需要的三种状态，避免把交互状态机直接泄漏到材质层。
 */
export enum FeatureFaceMaterialState {
  /** 默认隐藏态。 */
  Default = 'default',
  /** 悬浮态。 */
  Hover = 'hover',
  /** 选中态。 */
  Selected = 'selected',
}

/**
 * `FeatureFace` 材质配方。
 */
interface FeatureFaceMaterialRecipe {
  /** 材质名称。 */
  name: string
  /** 颜色。 */
  color: THREE.ColorRepresentation
  /** 透明度。 */
  opacity: number
  /** 是否参与已有场景深度测试。 */
  depthTest: boolean
  /** 是否把当前拓扑面深度写入深度缓冲。 */
  depthWrite: boolean
}

/** 默认隐藏态颜色。 */
const FEATURE_FACE_DEFAULT_COLOR = solidworksDefaultPreset.model3D.baseFaceColor;
/** 前景交互态颜色。 */
const FEATURE_FACE_FOREGROUND_COLOR = 0x53a9ff;

/**
 * 解析 `FeatureFace` 材质配方。
 *
 * 设计说明：
 * 1. Hover 拓扑面需要恢复实体深度关系，并把覆盖面深度写入缓冲，避免后续绘制穿透；
 * 2. Selected 继续作为稳定前景 Overlay，不参与实体深度竞争；
 * 3. 默认态保持透明且不写深度，用于兼容延迟挂载与状态机切换。
 *
 * @param state 材质状态。
 * @returns 对应的材质配方。
 */
function resolveFeatureFaceMaterialRecipe(
  state: FeatureFaceMaterialState,
): FeatureFaceMaterialRecipe {
  switch (state) {
    case FeatureFaceMaterialState.Hover:
      return {
        name: '特征面-悬浮材质',
        color: FEATURE_FACE_FOREGROUND_COLOR,
        opacity: 0.4,
        depthTest: true,
        depthWrite: true,
      };
    case FeatureFaceMaterialState.Selected:
      return {
        name: '特征面-选中材质',
        color: FEATURE_FACE_FOREGROUND_COLOR,
        opacity: 0.8,
        depthTest: false,
        depthWrite: false,
      };
    case FeatureFaceMaterialState.Default:
    default:
      return {
        name: '特征面-默认材质',
        color: FEATURE_FACE_DEFAULT_COLOR,
        opacity: 0,
        depthTest: false,
        depthWrite: false,
      };
  }
}

/**
 * 创建 `FeatureFace` 材质。
 *
 * 设计说明：
 * 1. Hover 使用 `depthTest=true + depthWrite=true`，恢复拓扑面的实体深度写入语义；
 * 2. Selected 使用 `depthTest=false + depthWrite=false`，继续保持稳定的前景 Overlay；
 * 3. 两种可见态都保留负向 polygon offset，避免拓扑代理与实体面完全共面。
 *
 * @param state 材质状态。
 * @returns 新创建的 `FeatureFace` 材质。
 */
export function createFeatureFaceMaterial(
  state: FeatureFaceMaterialState,
): THREE.MeshBasicMaterial {
  const recipe = resolveFeatureFaceMaterialRecipe(state);

  /**
   * FeatureFace 只是前景交互覆盖层，不参与实体光照。
   * 使用 MeshBasicMaterial 可以避免 WebGPU 为标准光照管线强制要求 normal 等顶点缓冲，
   * 从根上规避“拓扑面复用 GLB 子索引但源几何缺少对应 attribute”时的 CommandBuffer 失效。
   */
  return new THREE.MeshBasicMaterial({
    name: recipe.name,
    color: recipe.color,
    transparent: true,
    opacity: recipe.opacity,
    depthTest: recipe.depthTest,
    depthWrite: recipe.depthWrite,
    side: THREE.DoubleSide,
    polygonOffset: true,
    polygonOffsetFactor: -2,
    polygonOffsetUnits: -2,
  });
}
